import { Box, Grid } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { BomPartType } from 'bom/enums';
import { TextLine } from 'core/elements';
import { useAgGridClientTableContext } from 'core/states';
import { getAgGridTableCellData } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { QuotePartPricingGuidance } from 'quotes/components';
import { QuotePartColId } from 'quotes/components/QuotePartList';
import { externalSellPriceSourceFields } from 'quotes/constants';
import { QuotePartRecordType } from 'quotes/enums';
import { IQuotePart } from 'quotes/types';
import { pickPricingSourceFields } from 'quotes/utils';
import { Color, Direction, Level } from 'theme/enums';

import { ExternalPricingSourceTooltip } from './ExternalPricingSourceTooltip';
import MarginWithWarning from './MarginWithWarning';
import { PriceLockTooltip } from './PriceLockTooltip';
import PriceWithWarning from './PriceWithWarning';

interface IProps extends ICellRendererParams {
  onMarginClick?: (quotePart: IQuotePart) => void;
  isPricingEditable?: ({ data }) => boolean;
  minimumMarginThreshold?: number;
  treatAsCost?: boolean;
  showPriceGuidance?: boolean;
}

const PriceCell = ({
  onMarginClick,
  isPricingEditable,
  minimumMarginThreshold,
  treatAsCost = false,
  showPriceGuidance = true,
  ...props
}: IProps) => {
  const { t } = useTranslation();
  const { updateRows } = useAgGridClientTableContext();

  const data = getAgGridTableCellData(props, QuotePartColId.PRICE);
  if (!data) {
    return props?.value;
  }

  const { noQuote, partDiscountValue, pricingGroupDiscountValue, recordType, type } = data;

  if (noQuote) {
    return <TextLine text={t('labelNoQuote')} level={Level.BODY_LG} bold />;
  }

  if (BomPartType.DISCOUNT === type) {
    const discountValue =
      QuotePartRecordType.PRICING_GROUP_DISCOUNT === recordType
        ? pricingGroupDiscountValue
        : partDiscountValue;

    return <TextLine text={PricingHelper.discount(discountValue)} level={Level.BODY_LG} bold />;
  }

  const isAlternateVersionEnabled = Boolean(data.alternateVersionEnabled);

  const onPriceUpdateSuccess = (updatedLine: IQuotePart) => {
    updateRows({ update: [updatedLine] }, { refreshCells: true });
  };

  return (
    <Grid container alignItems="top" spacing={1}>
      <Grid xs={3}>
        <PriceLockTooltip quotePart={data} />
      </Grid>
      <Grid
        xs={isAlternateVersionEnabled && showPriceGuidance ? 7 : 9}
        sx={{ alignItems: 'flex-end' }}
      >
        <ExternalPricingSourceTooltip
          pricingData={pickPricingSourceFields(data, externalSellPriceSourceFields)}
          isAlternateVersionEnabled={isAlternateVersionEnabled && showPriceGuidance}
          color={Color.SUCCESS}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: Direction.COLUMN,
              justifyContent: 'flex-end',
              width: '100%',
              gap: 0.5,
            }}
          >
            <PriceWithWarning
              quotePart={data}
              treatAsCost={treatAsCost}
              showPriceGuidance={showPriceGuidance}
            />

            <MarginWithWarning
              quotePart={data}
              onMarginClick={onMarginClick}
              isPricingEditable={isPricingEditable}
              minimumMarginThreshold={minimumMarginThreshold}
            />
          </Box>
        </ExternalPricingSourceTooltip>
      </Grid>

      {isAlternateVersionEnabled && showPriceGuidance && (
        <Grid
          xs={2}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <QuotePartPricingGuidance quotePart={data} onPriceUpdateSuccess={onPriceUpdateSuccess} />
        </Grid>
      )}
    </Grid>
  );
};

export default PriceCell;
